import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { CSSTransition } from 'react-transition-group'
import { Link } from 'gatsby'
import { useLocation } from '@reach/router'

import * as spacing from 'styles/spacing'
import { black, greyLight, borderColor } from 'styles/colors'
import { animationCurve } from 'styles/global'
import { fontWeights, fontSizes } from 'styles/typography'

import Icon from 'components/Icon'
import RightChevron from 'icons/right-chevron.svg'
import LeftChevron from 'icons/left-chevron.svg'

import './styles.css'

const MenuItemContainer = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${black};
  width: 100%;
  padding: ${spacing.small} ${spacing.xLarge};
  font-size: 16px;

  ${props =>
    props.isLarge &&
    css`
      margin-bottom: ${spacing.small};

      &:last-child {
        margin-bottom: 0;
      }
    `}
`

const Dropdown = styled.div`
  position: relative;
  overflow: hidden;
  transition: height 500ms ${animationCurve};
`

const List = styled.div`
  width: 100%;
`

const ReturnLink = styled.button`
  display: flex;
  align-items: center;
  font-size: 16px;
  color: ${black};
  padding: ${spacing.small} ${spacing.xLarge};
  margin-bottom: ${spacing.medium};
  width: 100%;
`

const ReturnIcon = styled.div`
  display: flex;
  margin-right: ${spacing.tiny};
  margin-left: -${spacing.small};
`

const Divider = styled.div`
  height: 1px;
  background-color: ${borderColor};
  margin-top: ${spacing.large};
  margin-bottom: ${spacing.large};
`

const Eyebrow = styled.span`
  display: block;
  padding: ${spacing.small} ${spacing.xLarge};
  color: ${greyLight};
  text-transform: uppercase;
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.tiny};
  letter-spacing: 0.05em;
`

const Logo = styled.img`
  display: block;
  width: 100px;
  height: 27px;
  margin-left: ${spacing.xLarge};
  margin-bottom: ${spacing.small};
`

const Menu = () => {
  const [activeMenu, setActiveMenu] = useState('main')
  const [prevActiveMenu, setPrevActiveMenu] = useState(null)
  const location = useLocation()

  // 136px first menu height
  const [menuHeight, setMenuHeight] = useState(184)

  const tertiaryMenus = ['epen', 'epod', 'personalize', 'new']

  function calcHeight(el) {
    const height = el.offsetHeight
    setMenuHeight(height)
  }

  const MenuItem = ({ children, goToMenu, hasSubMenu, isLarge, ...rest }) => {
    const getElement = props => {
      const link = props.to || props.href
      if (link) {
        if (link.startsWith('http')) return 'a'
        console.log('nahh g')
        return Link
      }

      return 'button'
    }

    return (
      <MenuItemContainer
        as={getElement(rest)}
        onClick={() => {
          setPrevActiveMenu(activeMenu)
          goToMenu && setActiveMenu(goToMenu)
        }}
        isLarge={isLarge}
        {...rest}
      >
        {children}
        {hasSubMenu && <Icon icon={<RightChevron />} />}
      </MenuItemContainer>
    )
  }

  const Return = ({ goToMenu }) => (
    <ReturnLink
      onClick={() => {
        setPrevActiveMenu(activeMenu)
        goToMenu && setActiveMenu(goToMenu)
      }}
    >
      <ReturnIcon>
        <Icon icon={<LeftChevron />} />
      </ReturnIcon>
      All
    </ReturnLink>
  )

  return (
    <Dropdown style={{ height: menuHeight }}>
      {/* Main */}
      <CSSTransition
        in={activeMenu === 'main'}
        timeout={500}
        classNames="menu-primary"
        unmountOnExit
        onEnter={calcHeight}
      >
        <List>
          <MenuItem hasSubMenu isLarge goToMenu="shop">
            Shop
          </MenuItem>
          <MenuItem hasSubMenu isLarge goToMenu="save">
            Save
          </MenuItem>
          <MenuItem hasSubMenu isLarge goToMenu="about">
            About
          </MenuItem>
          <MenuItem hasSubMenu isLarge goToMenu="help">
            Help
          </MenuItem>
        </List>
      </CSSTransition>

      {/* Shop */}
      <CSSTransition
        in={activeMenu === 'shop'}
        timeout={500}
        classNames={
          tertiaryMenus.includes(activeMenu) || tertiaryMenus.includes(prevActiveMenu)
            ? 'menu-secondary-left'
            : 'menu-secondary'
        }
        unmountOnExit
        onEnter={calcHeight}
      >
        <List>
          <Return goToMenu="main" />
          <MenuItem goToMenu="epod" hasSubMenu>
            ePod 2
          </MenuItem>
          <MenuItem goToMenu="personalize" hasSubMenu>
            Personalize your ePod
          </MenuItem>
          <MenuItem goToMenu="new" hasSubMenu>
            New Launches
          </MenuItem>
        </List>
      </CSSTransition>

      {/* ePod */}
      <CSSTransition
        in={activeMenu === 'epod'}
        timeout={500}
        classNames="menu-tertiary"
        unmountOnExit
        onEnter={calcHeight}
      >
        <List>
          <Return goToMenu="shop" />
          <MenuItem data-tracking="nav--link--text--ePod_2" to="/vuse-epod-2/">
            Device
          </MenuItem>
          <MenuItem data-tracking="nav--link--text--ePod_Pods" to="/buy-online/vype-epod-vpro-cartridges/">
            Pods
          </MenuItem>
          <MenuItem data-tracking="nav--link--text--ePod_Charger" to="/buy-online/vype-epod-accessories/">
            Accessories
          </MenuItem>
        </List>
      </CSSTransition>

      {/* Personalize */}
      <CSSTransition
        in={activeMenu === 'personalize'}
        timeout={500}
        classNames="menu-tertiary"
        unmountOnExit
        onEnter={calcHeight}
      >
        <List>
          <Return goToMenu="shop" />
          <Logo src={`${process.env.BASE_PATH}/images/menu/vuse-x-you.png`} />
          <MenuItem
            href={`${location.origin}${process.env.BASE_PATH}/vusebyu/`}
            data-tracking="nav--link--text--VuseByU"
          >
            Experience vuseXu
          </MenuItem>
          <MenuItem
            href={`${location.origin}${process.env.BASE_PATH}/vusebyu/skins`}
            data-tracking="nav--link--text--ePod_Skins"
          >
            Shop ePod 2 Skins
          </MenuItem>
          <MenuItem
            href={`${location.origin}${process.env.BASE_PATH}/vusebyu/engravings/`}
            data-tracking="nav--link--text--ePod_Engrave"
          >
            Engrave Your ePod 2
          </MenuItem>
        </List>
      </CSSTransition>

      {/* New Launches */}
      <CSSTransition
        in={activeMenu === 'new'}
        timeout={500}
        classNames="menu-tertiary"
        unmountOnExit
        onEnter={calcHeight}
      >
        <List>
          <Return goToMenu="shop" />
          <MenuItem to="/vuse-epod-2/" data-tracking="nav--link--text--ePod_2">
            ePod 2
          </MenuItem>
          <MenuItem to="/buy-online/vype-epod-skins/" data-tracking="nav--link--text--ePod_Skins">
            Skins
          </MenuItem>
          <MenuItem
            href={`${location.origin}${process.env.BASE_PATH}/vusebyu/engravings/`}
            data-tracking="nav--link--text--ePod_Engrave"
          >
            Engraving
          </MenuItem>
          <MenuItem to="/buy-online/pod-caps/" data-tracking="nav--link--text--Pod_Caps">
            Pod Caps
          </MenuItem>
        </List>
      </CSSTransition>

      {/* Save */}
      <CSSTransition
        in={activeMenu === 'save'}
        timeout={500}
        classNames="menu-secondary"
        unmountOnExit
        onEnter={calcHeight}
      >
        <div>
          <List>
            <Return goToMenu="main" />

            <Eyebrow>Offers</Eyebrow>
            <MenuItem data-tracking="nav--link--text--bundle" to="/buy-online/epod-starter-bundle/">
              Starter Bundle
            </MenuItem>
            <MenuItem data-tracking="nav--link--text--multipack-3" to="/buy-online/multipack-3/">
              3 for $33 ePod Pods
            </MenuItem>
            <MenuItem data-tracking="nav--link--text--multipack-5" to="/buy-online/multipack-5/">
              5 for $50 ePod Pods
            </MenuItem>
          </List>
          <Divider />
          <List>
            <Eyebrow>Programs</Eyebrow>
            <MenuItem data-tracking="nav--link--text--subscribe" to="/subscribe/">
              Subscribe & Save (up to 30%)
            </MenuItem>
            <MenuItem data-tracking="nav--link--text--referral" to="/pass-on-the-savings/">
              Pass On The Savings (up to $200)
            </MenuItem>
            <MenuItem data-tracking="nav--link--text--vuse-plus" to="/vuse-plus-vape-deals/">
              Vuse+ Rewards
            </MenuItem>
          </List>
        </div>
      </CSSTransition>

      {/* About */}
      <CSSTransition
        in={activeMenu === 'about'}
        timeout={500}
        classNames="menu-secondary"
        unmountOnExit
        onEnter={calcHeight}
      >
        <List>
          <Return goToMenu="main" />
          <MenuItem data-tracking="nav--link--text--vuse-store" to="/flagship-store-toronto/">
            The Vuse Store
          </MenuItem>
        </List>
      </CSSTransition>

      {/* Help */}
      <CSSTransition
        in={activeMenu === 'help'}
        timeout={500}
        classNames="menu-secondary"
        unmountOnExit
        onEnter={calcHeight}
      >
        <List>
          <Return goToMenu="main" />
          <MenuItem data-tracking="nav--link--text--shipping" to="/shipping/">
            Shipping & Delivery
          </MenuItem>
          <MenuItem href="https://batsupport.zendesk.com/hc/en-us">Contact Us</MenuItem>
          <MenuItem href="https://batsupport.zendesk.com/hc/en-us">FAQ</MenuItem>
          <MenuItem data-tracking="nav--link--text--store-locator" to="/vape-stores/">
            Store Locator
          </MenuItem>
        </List>
      </CSSTransition>
    </Dropdown>
  )
}

export default Menu
